import { humanize } from "@/lib/lead-card";
import type { LeadSibling } from "@/lib/types";

/**
 * The other records with a lead's phone (ACL-336, ADR-115 in the platform repo), in a rep's words.
 * They share a number, and that is all the platform knows: nothing here calls two records the same
 * person or the same accident. Pure, for `test/same-phone.test.ts`.
 */

/** Said wherever the records are listed. */
export const SAME_PHONE_CAVEAT = "Same phone number only. It may be the same person or someone else.";

/** Said for a record the reader may not open: it exists, and its history is not theirs to read. */
export const NOT_YOURS = "Not in your contacts, so its history is not shown.";

/** Said for a deleted record: why it is still listed when it cannot be opened. */
export const DELETED_NOTE = "Deleted. Still counted when this number is checked for signed or do not call.";

/** The stages a rep must know about before they speak: the number is a client, or not to be called. */
const SIGNED = new Set(["signed", "signed_pending_intake"]);

/**
 * One line for the phone while it rings: how many other records share the number, and the ones
 * that change the call (signed, do not call). Null for none.
 */
export function samePhoneHeadline(
  siblings: Pick<LeadSibling, "stage" | "dnc">[],
): { text: string; flag: string | null } | null {
  const count = siblings.length;
  if (count === 0) return null;
  const signed = siblings.filter((s) => s.stage !== null && SIGNED.has(s.stage)).length;
  const dnc = siblings.filter((s) => s.dnc || s.stage === "dnc").length;
  const flags = [signed > 0 && `${signed} signed`, dnc > 0 && `${dnc} do not call`].filter(Boolean);
  return {
    text: count === 1 ? "1 other record shares this number" : `${count} other records share this number`,
    flag: flags.length > 0 ? flags.join(" · ") : null,
  };
}

/** "Daniela Lopez" -> "DL", "Ann" -> "A"; null without a name. */
export function initials(name: string | null): string | null {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  const first = parts[0]![0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]![0]! : "";
  return (first + last).toUpperCase();
}

/** A record's history as the card shows it. Null fields are ones the record does not have. */
export interface SiblingHistory {
  /** False only when core-api said so; an older core-api says nothing and the link stays. */
  mayOpen: boolean;
  /** An admin deleted it: listed for its stage and do-not-call, never opened. */
  deleted: boolean;
  name: string | null;
  outcome: string | null;
  reason: string | null;
  note: { title: string; body: string; at: string } | null;
}

const NOTE_TITLES: Record<string, string> = {
  note: "Last note",
  disposition: "Outcome note",
  handoff: "Handoff brief",
};

export function siblingHistory(sibling: LeadSibling): SiblingHistory {
  const name = [sibling.first_name, sibling.last_name].filter(Boolean).join(" ").trim();
  const note = sibling.last_note ?? null;
  const deleted = sibling.deleted === true;
  return {
    mayOpen: !deleted && sibling.may_open !== false,
    deleted,
    name: name || null,
    outcome: sibling.disposition_label ?? humanize(sibling.disposition ?? null),
    reason: sibling.decline_reason?.trim() || null,
    note: note && { title: NOTE_TITLES[note.kind] ?? "Last note", body: note.body, at: note.at },
  };
}
